import { useState, useEffect, useMemo } from "react";
import { format } from "date-fns";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  ArrowUpRight,
  ArrowDownRight,
  LifeBuoy,
  MoreHorizontal,
  Pencil,
  Repeat,
  X,
  XCircle,
  Clock,
  Truck,
  FileText,
  
} from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { OrderStatusBadge } from "@/components/ui/status-badge";
import { Order, Customer, Tractor, Driver } from "@/types/tms";
import { Badge } from "@/components/ui/badge";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useTrailerTypes, getTrailerTypeLabel } from "@/hooks/useTrailerTypes";
import { BolViewerDialog } from "@/components/orders/BolViewerDialog";
import { useTenant } from "@/hooks/useTenant";
import { TenderViewerDialog } from "@/components/orders/TenderViewerDialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { CancelOrderDialog } from "@/components/orders/CancelOrderDialog";
import { AssignRescueDialog } from "@/components/orders/AssignRescueDialog";
import { OrderNotesCard } from "@/components/orders/OrderNotesCard";
import { OrderDetailsSections } from "@/components/orders/OrderDetailsSections";
import { StarOrderButton } from "@/components/orders/StarOrderButton";
import { MultiLegRouteChain } from "@/components/orders/MultiLegRouteChain";
import { CancellationBanner } from "@/components/orders/CancellationBanner";
import { toast } from "sonner";
import { useOrderCancellation } from "@/hooks/useOrderDeletion";
import { useQuery as useLocQuery } from "@tanstack/react-query";
import {
  isLateDeparture,
  isLateArrival,
} from "@/lib/order-time";
import { ReadyTimeDisplay, DeliveryTimeDisplay } from "@/components/orders/OrderTimeDisplay";
import type { Location } from "@/types/tms";
import { resolveOrderEndpoints } from "@/lib/order-endpoints";
import { resolveParentOrderId } from "@/lib/orders-with-legs";
import { resolveLegStatus } from "@/lib/leg-status-resolve";
import { useOrderLegs } from "@/hooks/useOrderLegs";
import { TrailerArrivedNotice } from "@/components/orders/TrailerArrivedNotice";
import { useLegsWithArrivedTrailer } from "@/hooks/useLegsWithArrivedTrailer";

type OrderWithRelations = Order & {
  billing_party?: Customer | null;
  assigned_tractor?: Tractor | null;
  assigned_operator?: Driver | null;
};

interface OrderDetailSheetProps {
  order: OrderWithRelations | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEdit: (order: Order) => void;
  onView?: (order: Order) => void;
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between items-start py-2">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-medium text-right max-w-[60%]">{value || "—"}</span>
    </div>
  );
}


export function OrderDetailSheet({ order, open, onOpenChange, onEdit, onView }: OrderDetailSheetProps) {
  const { data: trailerTypes } = useTrailerTypes();
  const { tenantId } = useTenant();
  const navigate = useNavigate();
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showRescueDialog, setShowRescueDialog] = useState(false);
  const [actionsMenuOpen, setActionsMenuOpen] = useState(false);
  const { cancelOrderAsync, isCanceling } = useOrderCancellation();

  // Rows coming from `orders_with_legs` (Schedule, board views) carry the LEG
  // id in `id` and the real order id in `parent_order_id`. Every order-scoped
  // query/action below must use the parent id.
  const orderId = resolveParentOrderId(order as any);

  // Tenant locations cache — used to resolve each stop's IANA timezone for
  // wall-clock display ("airline times").
  const { data: locations } = useLocQuery({
    queryKey: ["locations"],
    queryFn: async () => {
      const { data } = await supabase.from("locations").select("*").order("short_code");
      return (data ?? []) as Location[];
    },
    staleTime: 5 * 60 * 1000,
  });

  const { data: trailerInfo } = useQuery({
    queryKey: ["trailer-info", order?.trailer_id, tenantId],
    enabled: !!order?.trailer_id && open && !!tenantId,
    queryFn: async () => {
      const { data } = await supabase
        .from("trailers")
        .select("load_status, trailer_type_id")
        .eq("trailer_id", order!.trailer_id!)
        .eq("tenant_id", tenantId!)
        .maybeSingle();
      return data ?? null;
    },
  });
  const trailerLoadStatus = trailerInfo?.load_status ?? null;
  const trailerTypeIdFallback = trailerInfo?.trailer_type_id ?? null;

  // Same query (and cache entry) as OrderDetail.tsx, so a leg never reads
  // differently here than on the detail page.
  const { data: orderLegs } = useOrderLegs(orderId, { enabled: open });
  const legsWithArrivedTrailer = useLegsWithArrivedTrailer(orderLegs ?? [], order?.status);
  const childLegs = useMemo(() => {
    if (!orderLegs) return undefined;
    const parentIsMultiLeg = orderLegs.length > 1;
    return orderLegs.map((l) => ({
      ...l,
      unique_id: `Leg ${l.leg_sequence}`,
      status: resolveLegStatus(l, order?.status, parentIsMultiLeg) ?? "booked",
    }));
  }, [orderLegs, order?.status]);
  const isMultiLeg = (childLegs?.length ?? 0) > 1;

  const openCancelDialog = () => {
    setActionsMenuOpen(false);
    window.setTimeout(() => setShowCancelDialog(true), 0);
  };

  const openRescueDialog = () => {
    setActionsMenuOpen(false);
    window.setTimeout(() => setShowRescueDialog(true), 0);
  };

  // Re-render every 60s so lateness styling updates without a manual reload.
  const [, setNowTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setNowTick((n) => n + 1), 60_000);
    return () => window.clearInterval(id);
  }, []);

  const lateDep = order ? isLateDeparture(order) : false;
  const lateArr = order ? isLateArrival(order) : false;
  const warningTextClass = "text-[hsl(var(--aurora-status-warning))]";

  const { data: recurringTemplate } = useQuery({
    queryKey: ["recurring-template", order?.recurring_order_template_id],
    queryFn: async () => {
      const { data } = await supabase
        .from("recurring_order_templates")
        .select("unique_id")
        .eq("id", order!.recurring_order_template_id!)
        .maybeSingle();
      return data;
    },
    enabled: !!order?.recurring_order_template_id,
  });

  // Resolve origin/destination short codes using the canonical resolver against
  // the shared `locations` cache. See src/lib/order-endpoints.ts.
  const { from: originEndpoint, to: destEndpoint } = resolveOrderEndpoints(order, locations);

  // BOL/Tender counts
  const { data: bolCount } = useQuery({
    queryKey: ["bol-count", orderId],
    queryFn: async () => {
      const { count } = await supabase
        .from("order_bol_images")
        .select("*", { count: "exact", head: true })
        .eq("order_id", orderId!);
      return count || 0;
    },
    enabled: !!orderId && open,
  });

  const { data: tenderCount } = useQuery({
    queryKey: ["tender-count", orderId],
    queryFn: async () => {
      const { count } = await supabase
        .from("order_tender_images")
        .select("*", { count: "exact", head: true })
        .eq("order_id", orderId!);
      return count || 0;
    },
    enabled: !!orderId && open,
  });

  const handleCancel = async (payload: import("./CancelOrderDialog").CancelConfirmPayload) => {
    if (!orderId) return;
    try {
      await cancelOrderAsync(orderId, {
        reason: payload.reasonLabel,
        notes: payload.otherText,
        changeEventReason: payload.reason,
        changeEventOther: payload.otherText,
      });
      setShowCancelDialog(false);
    } catch {
      // toast handled inside useOrderCancellation
    }
  };

  if (!order) return null;

  return (
    <>
    <Sheet open={open} onOpenChange={onOpenChange} modal={false}>
      <SheetContent
        side="right"
        className="w-full lg:w-[440px] lg:max-w-[440px] max-w-full overflow-y-auto [&>button.absolute]:hidden p-0"
        onInteractOutside={(event) => {
          if (actionsMenuOpen || showCancelDialog || showRescueDialog) {
            event.preventDefault();
          }
        }}
      >
        {/* Action bar */}
        <div className="flex items-center justify-between px-4 pt-4 pb-3">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onOpenChange(false)}>
            <X className="h-4 w-4" />
          </Button>
          <div className="flex items-center gap-1.5">
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { onOpenChange(false); onEdit(order); }}>
              <Pencil className="h-3.5 w-3.5" />
            </Button>
            {order.status !== "canceled" && (
              <DropdownMenu open={actionsMenuOpen} onOpenChange={setActionsMenuOpen}>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {false && order?.status === "in_transit" && order?.assigned_tractor_id && (
                    <DropdownMenuItem onSelect={(e) => { e.preventDefault(); openRescueDialog(); }}>
                      <LifeBuoy className="h-4 w-4 mr-2" />
                      Assign Rescue
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem
                    className="text-destructive focus:text-destructive"
                    onSelect={(e) => {
                      e.preventDefault();
                      openCancelDialog();
                    }}
                  >
                    <XCircle className="h-4 w-4 mr-2" />
                    Cancel Order
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            {onView && (
              <Button variant="outline" size="sm" onClick={() => { onOpenChange(false); onView(order); }}>
                View details
                <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
              </Button>
            )}
          </div>
        </div>

        {/* Header */}
        <div className="px-4 pb-4">
          <div className="flex items-center gap-1.5">
            <h2 className="text-lg font-semibold font-mono">{order.unique_id}</h2>
            <StarOrderButton orderId={orderId!} size="sm" />
          </div>
          <div className="pt-2 empty:hidden">
            <TrailerArrivedNotice legs={legsWithArrivedTrailer} />
          </div>
          {order.status === "canceled" ? (
            <div className="pt-2">
              <CancellationBanner
                order={order as any}
                isMultiLeg={isMultiLeg}
                recurringTemplate={recurringTemplate ?? null}
              />
            </div>
          ) : (
            <div className="flex items-center gap-2 pt-1 flex-wrap">
              <OrderStatusBadge status={order.status} late={lateDep || lateArr} legs={order.legs} />
              {isMultiLeg && (
                <Badge variant="secondary" className="text-xs font-medium">
                  Multi-leg
                </Badge>
              )}
              {order.trip_type && (
                <Badge variant="secondary" className="text-xs font-medium">
                  {order.trip_type}
                </Badge>
              )}
              {(order as any).external_order_id && (
                <Badge variant="info" className="text-xs font-medium">
                  API Order
                </Badge>
              )}
              {recurringTemplate && order.recurring_order_template_id && (
                <Link to={`/templates/${order.recurring_order_template_id}`}>
                  <Badge variant="outline" className="gap-1 text-xs font-normal hover:bg-accent cursor-pointer">
                    <Repeat className="h-3 w-3" />
                    {recurringTemplate.unique_id}
                  </Badge>
                </Link>
              )}
            </div>
          )}
        </div>

        {/* Sections */}
        <div className="px-4 pb-4 space-y-4">
          {/* Schedule */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">
                Order Summary
                {isMultiLeg && (
                  <span className="text-xs text-muted-foreground font-normal ml-1">
                    ({childLegs!.length} legs)
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isMultiLeg ? (
                <MultiLegRouteChain
                  parentOrder={order}
                  legs={childLegs as any}
                  onSelectLeg={(legId) => {
                    onOpenChange(false);
                    navigate(`/orders/${orderId}?leg=${legId}`);
                  }}
                />
              ) : (
                <>
              {/* Route info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <div className="flex items-center gap-1.5">
                    <ArrowUpRight className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-sm font-bold font-mono text-foreground">
                      {originEndpoint.label}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {[order.ship_from_street_address, order.ship_from_city, order.ship_from_province_state].filter(Boolean).join(", ")}
                  </p>
                </div>
                <div className="space-y-1">
                  <div className="flex items-center gap-1.5">
                    <ArrowDownRight className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-sm font-bold font-mono text-foreground">
                      {destEndpoint.label}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {[order.ship_to_street_address, order.ship_to_city, order.ship_to_province_state].filter(Boolean).join(", ")}
                  </p>
                </div>
              </div>

              {/* Times below route */}
              <div className="border-t mt-3 pt-3 grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Ready on</p>
                  <ReadyTimeDisplay order={order} variant="card" cardSize="sm" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Delivering on</p>
                  <DeliveryTimeDisplay order={order} variant="card" cardSize="sm" />
                </div>
              </div>
                </>
              )}
            </CardContent>
          </Card>


          {/* Details */}
          <OrderDetailsSections
            order={order}
            trailerTypes={trailerTypes}
            trailerLoadStatus={trailerLoadStatus}
            trailerTypeIdFallback={trailerTypeIdFallback}
            hideLegSpecific={isMultiLeg}
            recurringTemplate={recurringTemplate}
            compact
            legTrailers={
              isMultiLeg && childLegs
                ? childLegs.map((l: any) => ({
                    leg_sequence: l.leg_sequence,
                    trailer_id: l.trailer_id ?? null,
                    trailer_type_id: l.trailer_type_id ?? null,
                  }))
                : undefined
            }
          />

          {/* Documents */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Documents
                {((bolCount || 0) + (tenderCount || 0) > 0) && (
                  <span className="ml-1 text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded-full">
                    {(bolCount || 0) + (tenderCount || 0)}
                  </span>
                )}
              </CardTitle>
            </CardHeader>
             <CardContent className="space-y-3">
               <div className="border rounded-lg p-3 space-y-1.5">
                 <div className="flex items-center justify-between">
                   <h4 className="text-sm font-medium">Bill of Lading</h4>
                   <BolViewerDialog orderId={orderId!} orderUniqueId={order.unique_id} />
                 </div>
                 <p className="text-xs text-muted-foreground">
                   {bolCount ? `${bolCount} document${bolCount > 1 ? "s" : ""} uploaded` : "No documents uploaded"}
                 </p>
               </div>
               <div className="border rounded-lg p-3 space-y-1.5">
                 <div className="flex items-center justify-between">
                   <h4 className="text-sm font-medium">Tender</h4>
                   <TenderViewerDialog orderId={orderId!} orderUniqueId={order.unique_id} />
                 </div>
                 <p className="text-xs text-muted-foreground">
                   {tenderCount ? `${tenderCount} document${tenderCount > 1 ? "s" : ""} uploaded` : "No documents uploaded"}
                 </p>
               </div>
             </CardContent>
          </Card>

          {/* Notes */}
          <OrderNotesCard orderId={orderId!} compact />
        </div>
      </SheetContent>
    </Sheet>

    <CancelOrderDialog
      open={showCancelDialog}
      onOpenChange={setShowCancelDialog}
      orderId={order?.unique_id ?? ""}
      onConfirm={(payload) => { void handleCancel(payload); }}
      isPending={isCanceling}
    />

    <AssignRescueDialog
      open={showRescueDialog}
      onOpenChange={setShowRescueDialog}
      orderId={orderId ?? ""}
      orderUniqueId={order?.unique_id}
      tractorName={order?.assigned_tractor?.name}
      currentRescueOperatorId={(order as any)?.rescue_operator_id}
    />
    </>
  );
}
